import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const currentCategory = categoryMap[activeSlug] || categories[0];
  const articles = getArticlesByCategory(activeSlug);

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 内容分区导航 | 国产内容分类指南`}
        description={`${currentCategory.description} 浏览${currentCategory.name}分类下的全部${currentCategory.articleCount}篇精选文章，获取最新最全的内容资源。`}
        keywords={`${currentCategory.name},内容分区,分类导航,内容指南,${currentCategory.name}资源,${currentCategory.name}推荐`}
        canonicalUrl={window.location.origin + (__BASE_PATH__ || '') + '/category/' + activeSlug}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: window.location.origin + (__BASE_PATH__ || '') + '/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: currentCategory.name,
            },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Category Header */}
          <section className="bg-background-100/50 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory.name },
                ]}
              />
              <div className="flex items-center gap-4 mt-2">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${currentCategory.icon} text-2xl text-accent-900`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1 max-w-2xl">
                    {currentCategory.description}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="border-b border-background-200/70 bg-background-50 sticky top-16 z-30">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 overflow-x-auto py-2 scrollbar-hide">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Article List */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
            {articles.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-foreground-600">
                    共 <strong className="text-foreground-950">{articles.length}</strong> 篇文章
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {articles.map((article) => (
                    <ArticleCard key={article.id} {...article} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">该分类暂无文章</p>
                <Link
                  to="/"
                  className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
                >
                  返回首页
                </Link>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}